import React, { JSX, useState, useCallback, useEffect, memo, forwardRef, useImperativeHandle } from 'react';
import { View, Text, Image, Pressable, Platform, ViewStyle } from 'react-native';
import { GoogleMap, useJsApiLoader, Marker, OverlayView, Circle } from '@react-google-maps/api';
import SearchComponent from '@components/Global/SearchComponent/SearchBar';
import LottieView from '@utils/lottieAnimation';
import { LinearGradient } from 'expo-linear-gradient';
import styles from '@stylesheet';
import * as Animatable from 'react-native-animatable';
import Button from '@components/UI/Button/Button';
import { AntDesign, Entypo } from '@expo/vector-icons';
import * as Location from 'expo-location';
import { SmartSaaSIcon } from '@assets/--native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';

// Current Location Marker Component
const CurrentLocationMarker = memo(() => {
  const theme = themeSelector();

  return (
    <View style={{
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Pulsing outer circle */}
      <Animatable.View
        animation={{
          from: {
            opacity: 0.8,
            scale: 1,
          },
          to: {
            opacity: 0,
            scale: 2,
          },
        }}
        iterationCount="infinite"
        duration={1500}
        style={{
          position: 'absolute',
          width: 40,
          height: 40,
          backgroundColor: '#3b82f6',
          borderRadius: 20,
          opacity: 0.3,
        }}
      />

      {/* Inner blue dot */}
      <View style={{
        width: 20,
        height: 20,
        backgroundColor: '#3b82f6',
        borderRadius: 10,
        borderWidth: 3,
        borderColor: 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 5,
      }}>
        <View style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 6,
          height: 6,
          backgroundColor: 'white',
          borderRadius: 3,
          transform: [{ translateX: -3 }, { translateY: -3 }],
        }} />
      </View>
    </View>
  );
});

// Business Marker Component
const BusinessMarker = memo<{ business: any }>(({ business }) => {
  const theme = themeSelector();
  const [showDetail, setShowDetail] = useState(false);
  
  const businessType = business.businessType || 'business';
  const businessName = business.businessName || 'Unknown Business';
  const businessDescription = business.businessDescription || '';
  const services = business.services || [];
  
  // Color mapping for different business types
  const getBusinessColor = (type: string) => {
    const colors: { [key: string]: string } = {
      'startup': '#8b5cf6', // purple
      'small_business': '#10b981', // green
      'medium_business': '#3b82f6', // blue
      'enterprise': '#f59e0b', // orange
      'freelancer': '#ec4899', // pink
      'consultant': '#6366f1', // indigo
      'other': '#6b7280' // grey
    };
    return colors[type] || colors.other;
  };
  
  const businessColor = getBusinessColor(businessType);

  //TODO implement no network placeholder 
  
  return (
    <Pressable 
      onPress={() => setShowDetail(!showDetail)}
      style={{
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Business Pin */}
      <View style={{
        width: 40,
        height: 40,
        backgroundColor: businessColor,
        borderRadius: 20,
        borderWidth: 3,
        borderColor: 'white',
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
        elevation: 5,
      }}>
        <Text style={{
          color: 'white',
          fontSize: 12,
          fontWeight: 'bold',
        }}>
          {businessName.charAt(0).toUpperCase()}
        </Text>
      </View>
      
      {/* Business Detail Card */}
      {showDetail && (
        <Animatable.View 
          animation="fadeInUp" 
          duration={300}
          style={{
            position: 'absolute',
            top: -220,
            left: -125,
            width: 250,
            backgroundColor: theme ? 'white' : '#374151',
            borderRadius: 12,
            padding: 16,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 10,
            borderWidth: 1,
            borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
          }}
        >
          <Pressable 
            onPress={() => setShowDetail(false)}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: 12,
              backgroundColor: 'rgba(0,0,0,0.1)',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AntDesign 
              name="close" 
              size={14} 
              color={theme ? '#666' : '#ccc'} 
            />
          </Pressable>
          
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <View style={{
              width: 12,
              height: 12,
              backgroundColor: businessColor,
              borderRadius: 6,
              marginRight: 8,
            }} />
            <Text style={{
              fontSize: 16,
              fontWeight: 'bold',
              color: theme ? '#1f2937' : '#f9fafb',
              flex: 1,
            }} numberOfLines={2}>
              {businessName}
            </Text>
          </View>
          
          {businessDescription && (
            <Text style={{
              fontSize: 12,
              color: theme ? '#6b7280' : '#9ca3af',
              marginBottom: 8,
              lineHeight: 16,
            }} numberOfLines={3}>
              {businessDescription}
            </Text>
          )}
          
          {services.length > 0 && (
            <View style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              marginBottom: 8,
            }}>
              {services.slice(0, 3).map((service: string, index: number) => (
                <View 
                  key={index}
                  style={{
                    backgroundColor: `${businessColor}20`,
                    paddingHorizontal: 6,
                    paddingVertical: 2,
                    borderRadius: 6,
                    marginRight: 4,
                    marginBottom: 4,
                  }}
                >
                  <Text style={{
                    fontSize: 10,
                    color: businessColor,
                    fontWeight: '500',
                  }}>
                    {service}
                  </Text>
                </View>
              ))}
              {services.length > 3 && (
                <View style={{
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  paddingHorizontal: 6,
                  paddingVertical: 2,
                  borderRadius: 6,
                }}>
                  <Text style={{
                    fontSize: 10,
                    color: theme ? '#6b7280' : '#9ca3af',
                    fontWeight: '500',
                  }}>
                    +{services.length - 3}
                  </Text>
                </View>
              )}
            </View>
          )}
          
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 8,
          }}>
            <Text style={{
              fontSize: 10,
              color: theme ? '#9ca3af' : '#6b7280',
              textTransform: 'capitalize',
            }}>
              {businessType.replace('_', ' ')}
            </Text>
            
            <Pressable style={{
              backgroundColor: businessColor,
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 6,
            }}>
              <Text style={{
                color: 'white',
                fontSize: 12,
                fontWeight: '500',
              }}>
                Contact
              </Text>
            </Pressable>
          </View>
          
          {/* Arrow pointing down */}
          <View style={{
            position: 'absolute',
            bottom: -6,
            left: '50%',
            marginLeft: -6,
            width: 12,
            height: 12,
            backgroundColor: theme ? 'white' : '#374151',
            transform: [{ rotate: '45deg' }],
            borderBottomWidth: 1,
            borderRightWidth: 1,
            borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
          }} />
        </Animatable.View>
      )}
    </Pressable>
  );
});

type MapViewProps = {
  companyDetails?: any,
  /**
   * Show a search bar on the map
   * @default false
   */ 
  searchBar?: boolean,
  /**
   * Location details for the map
   * @default undefined
   * @example { lat: 51.496600, lng: -0.381150 }
   * @type { lat: number, lng: number }
   * @required
   * @description The location details for the map
   */ 
  locationDetails?: { lat: number, lng: number },
  /**
   * Show a marker on the map (for business location)
   */
  marker?: boolean,
  /**
   * Render a heatmap on the map
   * @param map - The map object
   * @returns JSX.Element
   * @default undefined
   */
  renderHeatmap?: (map: any) => JSX.Element,
  heatmapData?: any[],
  noFeatures?: boolean,
  style?: ViewStyle,
  /**
   * Networking businesses to display on the map
   */
  networkingBusinesses?: any[],
  /**
   * Show current location marker
   * @default true
   */
  showCurrentLocationMarker?: boolean,
  /**
   * Callback when region/center changes
   */
  onRegionChangeComplete?: (region: any) => void,
  /**
   * Show a fixed pin at the center of the map
   */
  showCenterPin?: boolean
}

type GMap = {
  panTo: (location: any) => void,
  forceUpdate: () => void
}

const MapView = forwardRef<GMap, MapViewProps>((props, ref) => {
  const { companyDetails, searchBar, locationDetails, marker, renderHeatmap, heatmapData, noFeatures, style, networkingBusinesses = [], showCurrentLocationMarker = true, onRegionChangeComplete, showCenterPin = false } = props;
  const [map, setMap] = useState(null)
  const view: React.CSSProperties = { width: '100%', height: '100%', borderRadius: 20, ...style as React.CSSProperties };
  const [chatOpen, setChatOpen] = useState(Platform.OS === 'web' ? true : false);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  let logo = currentCompany?.logo || <SmartSaaSIcon />;
  const theme = themeSelector();

  // Default fallback location (London)
  const defaultLocation = {
    lat: 51.496600,
    lng: -0.381150
  };

  const [currentLocation, setCurrentLocation] = useState(defaultLocation);
  const [locationLoading, setLocationLoading] = useState(true);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyAznGh_h4mH0meG5V6hlTlLVhVithzlUJM'
  })

  // Get user's current location on mount
  useEffect(() => {
    (async () => {
      try {
        let { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          console.log('Permission to access location was denied');
          setLocationLoading(false);
          return;
        }

        let location = await Location.getCurrentPositionAsync({});
        setCurrentLocation({
          lat: location.coords.latitude,
          lng: location.coords.longitude
        });
        setLocationLoading(false);
      } catch (error) {
        console.log('Error getting location:', error);
        setLocationLoading(false);
      }
    })();
  }, []);

  // Extract business location from companyDetails
  const businessLocation = React.useMemo(() => {
    if (!companyDetails) return null;

    // Check if company has location coordinates
    if (companyDetails.location?.coordinates && Array.isArray(companyDetails.location.coordinates)) {
      return {
        lat: companyDetails.location.coordinates[1],
        lng: companyDetails.location.coordinates[0]
      };
    }

    // Check if company has lat/lng directly
    if (companyDetails.latitude && companyDetails.longitude) {
      return {
        lat: companyDetails.latitude,
        lng: companyDetails.longitude
      };
    }

    return null;
  }, [companyDetails]);

  useImperativeHandle(ref, () => ({
    panTo: (location) => {
      map.panTo(location);
      map.zoom = 15;
    },
    forceUpdate: () => {
      map.forceUpdate();
    }
  }));

  useEffect(()=>{
    locationDetails && map && map.panTo(locationDetails)
  }, [locationDetails, map])

  // Determine map center: prioritize locationDetails, then business location, then current location
  const center = locationDetails || businessLocation || currentLocation;

  const onLoad = useCallback(function callback(map) {
    const bounds = new window.google.maps.LatLngBounds(center);
    map.fitBounds(bounds);
    setMap(map)
  }, [center])

  const onUnmount = useCallback(function callback(map) {
    setMap(null)
  }, []);

  const onDragEnd = useCallback(() => {
    if (map && onRegionChangeComplete) {
      const center = map.getCenter();
      if (center) {
        onRegionChangeComplete({
          latitude: center.lat(),
          longitude: center.lng(),
          latitudeDelta: 0,
          longitudeDelta: 0
        });
      }
    }
  }, [map, onRegionChangeComplete]);

  const MarkerLoad = ({ mapItem }) => {
    // Theme-aware colors
    const overlayBgColor = !theme ? 'rgba(74, 158, 255, 0.9)' : 'rgba(90, 192, 234, 0.7)'; // Dark : Light
    const cardBgColor = !theme ? '#374151' : '#ececec'; // Dark : Light
    const textColor = !theme ? '#f9fafb' : '#1f2937'; // Dark : Light
    const gradientColors = !theme ? ['#374151', 'transparent'] as const : ['white', 'transparent'] as const; // Dark : Light
    const triangleBgColor = !theme ? '#374151' : '#ececec'; // Dark : Light

    return  (
      <Animatable.View useNativeDriver animation={'bounceInDown'} style={[styles.centerEverything, {
        padding: 10,
        borderRadius: 20,
        shadowOpacity: 0.1,
        elevation: 1,
        shadowRadius: 20,
        height: 200,
        width: 250,
        left: -125,
        top: -200,
        backgroundColor: overlayBgColor
      }]}>
        <LinearGradient colors={gradientColors} style={{
          backgroundColor: cardBgColor,
          borderRadius: 20,
          width: 240,
          height: 190,
          padding: 10,
          marginTop: -5,
          flexDirection: 'row'
        }}>
          <View style={{ flex: .8, elevation: 1, shadowRadius: 20, shadowOpacity: 0.2, borderRadius: 20 }}>{
            companyDetails ?
              <Image source={{ uri: currentCompany?.logo }} style={{ minWidth: 100, minHeight: 100, resizeMode: 'contain', borderRadius: 20 }} />
              :
            <SmartSaaSIcon style={[styles.centerEverything, { minWidth: 100, flex: 1 }]} />
          }
            {
              companyDetails ?
                <Text adjustsFontSizeToFit style={{ fontWeight: 'bold', textAlign: 'center', top: -3, color: textColor }}>
                  {companyDetails?.companyName?.toUpperCase()}
                </Text>
                :
                null
            }
          </View>
          <View style={[styles.centerEverything, { flex: 0.2, gap: 10, paddingLeft: 5, justifyContent: "space-around", alignSelf: 'center' }]}>
            <LottieView source={require('@assets/animations/achievementTrophy.json')} style={{ width: '100%' }} autoPlay />
            <Text adjustsFontSizeToFit style={{ fontWeight: 'bold', color: textColor }}>6794</Text>
            <View style={{ flex: 0.1 }} />
            <Pressable onPress={() => { console.log('get in touch pressed') }}>
              <LottieView source={require('@assets/animations/LordIcons/forum.json')} style={{ width: '80%' }} autoPlay />
            </Pressable>
            <Pressable onPress={() => { console.log('storefront pressed') }}>
              <LottieView source={require('@assets/animations/LordIcons/store.json')} style={{ width: '80%' }} autoPlay />
            </Pressable>
          </View>
        </LinearGradient>
        <View style={{
          transform: [{ rotateZ: '45deg' }],
          width: 50,
          height: 50,
          backgroundColor: triangleBgColor,
          marginTop: -40,
          zIndex: -1
        }}></View>
      </Animatable.View >
    )
  }

  const circleOptions = {
    strokeColor: '#5ac0eab3',
    strokeOpacity: 0.8,
    strokeWeight: 50,
    fillColor: '#5ac0ea',
    fillOpacity: 0.35,
    clickable: false,
    draggable: false,
    editable: false,
    visible: true,
    radius: 10,
    zIndex: 1
  }

  return isLoaded && (
    <View style={{
      flex: 1, alignSelf: 'stretch', shadowOpacity: 0.1,
      elevation: 1,
      shadowRadius: 20, borderRadius: 20, overflow:"hidden", flexDirection: 'column-reverse', minHeight: 180 }} >
      <GoogleMap
        mapContainerStyle={view}
        center={center}
        zoom={15}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onDragEnd={onDragEnd}
        options={{
          fullscreenControl: false,
          zoomControl: false,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: noFeatures ? "off" : "on" }]
            },
            // Apply dark theme styles when theme is false (dark mode)
            ...(!theme ? [
              // Dark theme map styles
              { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
              { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
              { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
              { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
              { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
              { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
              { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
              { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
              { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
              { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
              { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
              { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
              { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
              { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
              { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
              { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
              { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
              { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#17263c" }] }
            ] : [
              // Light theme map styles
              { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
              { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
              { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
              { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
              { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
              { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
              { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
              { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
              { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
              { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
              { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
              { featureType: "road.local", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] },
              { featureType: "transit.line", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
              { featureType: "transit.station", elementType: "geometry", stylers: [{ color: "#eeeeee" }] },
              { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
              { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#9e9e9e" }] }
            ])
          ],
          mapTypeControl: false,
          streetViewControl: false,
          minZoom: 8,
          maxZoom: 17
        }}
      >
        {heatmapData && renderHeatmap && renderHeatmap(map)}

        {/* Business Location Marker and Circle */}
        {marker && businessLocation && (
          <>
            <Circle center={businessLocation} options={{
              ...circleOptions,
              strokeColor: !theme ? '#4a9eff' : '#5ac0eab3', // Dark theme : Light theme
              fillColor: !theme ? '#4a9eff' : '#5ac0ea'
            }} />
            <OverlayView position={businessLocation} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <MarkerLoad mapItem={{ logo: logo }} />
            </OverlayView>
          </>
        )}

        {/* Current Location Marker */}
        {showCurrentLocationMarker && !locationLoading && currentLocation && (
          <OverlayView position={currentLocation} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <CurrentLocationMarker />
          </OverlayView>
        )}
        
        {/* Networking Business Markers */}
        {networkingBusinesses.map((business, index) => {
          if (!business.location?.coordinates) return null;
          
          const businessPosition = {
            lat: business.location.coordinates[1], // latitude
            lng: business.location.coordinates[0]  // longitude
          };
          
          return (
            <OverlayView 
              key={business._id || index} 
              position={businessPosition} 
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <BusinessMarker business={business} />
            </OverlayView>
          );
        })}
        
        {/* Location Details Circle (when manually set) */}
        {locationDetails && (
          <Circle center={locationDetails} options={{
            ...circleOptions,
            strokeColor: !theme ? '#ef4444' : '#f87171', // Red for manual location
            fillColor: !theme ? '#ef4444' : '#f87171'
          }} />
        )}
      </GoogleMap>

      {showCenterPin && (
        <View
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginLeft: -24,
            marginTop: -48,
            zIndex: 10,
            elevation: 10,
            pointerEvents: 'none'
          }}
        >
          <Entypo name="location-pin" size={48} color={theme ? '#ef4444' : '#f87171'} />
          <View style={{
            position: 'absolute',
            bottom: 4,
            left: 24,
            width: 10,
            height: 4,
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: 5,
            transform: [{ translateX: -5 }]
          }} />
        </View>
      )}

      {
        searchBar &&
        <View style={{ position: 'absolute', width: '100%', top: 0, flexDirection: 'row' }}>
        <SearchComponent minWidth={600} key={'userSearch'} placeHolder='Search for users' searchState={undefined} setSearchState={undefined}
          customIcons={[
            { functionName: 'addUser', function: () => undefined, icon: <AntDesign name="adduser" size={24} color={theme ? "black" : "white"} /> },
            { functionName: 'chat', function: () => setChatOpen(!chatOpen), icon: <AntDesign name="wechat" size={24} color={theme ? "black" : "white"} /> },
            { functionName: 'settings', function: () => setChatOpen(!chatOpen), icon: <AntDesign name="setting" size={24} color={theme ? "black" : "white"} /> },
          ]} />
        <Button buttonText="" icon={'wechat'} onPress={() => setChatOpen(!chatOpen)} customStyle={{ top: 0, right: 0, maxWidth: 50 }} />
          </View>
      }
    </View>
  )
})
export default MapView;